<script setup lang="ts">
import PopOver from '#components/popover/PopOver.vue';
import { useLanguage } from '#composables/useLanguage';
import { ref, watch } from 'vue';
import type { ChipOption } from './ToggleChips.model';

const {
  minSelectedRequired = 0,
  direction = 'row',
  options,
  requiredOptions = [],
  isMultiple = true
} = defineProps<{
  minSelectedRequired?: number;
  direction?: 'row' | 'col';
  options: ChipOption[];
  requiredOptions?: string[];
  isMultiple?: boolean;
}>();

const emit = defineEmits<{
  'update:options': [value: string[], selectedOptions?: string];
}>();

const { t } = useLanguage();

const selectedOptions = ref<string[]>(
  isMultiple
    ? options.filter((option) => option.checked).map((option) => option.value)
    : [options.find((option) => option.checked)?.value].filter(Boolean)
);

watch(
  () => options,
  () => {
    selectedOptions.value = selectedOptions.value.filter((value) => options.some((option) => option.value === value));
    for (const option of options) {
      if (option.checked && !selectedOptions.value.includes(option.value)) {
        selectedOptions.value.push(option.value);
      } else if (!option.checked && selectedOptions.value.includes(option.value)) {
        selectedOptions.value.splice(selectedOptions.value.indexOf(option.value), 1);
      }
    }
  }
);

const handleOptionClick = (optionValue: string) => {
  const isAlreadySelected = selectedOptions.value.includes(optionValue);
  const minSelectedRequiredVal = minSelectedRequired ?? 0;

  if (
    ((!isMultiple || selectedOptions.value.length <= minSelectedRequiredVal) && isAlreadySelected) ||
    requiredOptions.includes(optionValue)
  ) {
    return;
  }

  if (isAlreadySelected) {
    selectedOptions.value = selectedOptions.value.filter((value) => value !== optionValue);
  } else if (isMultiple) {
    selectedOptions.value = [...selectedOptions.value, optionValue];
  } else {
    selectedOptions.value = [optionValue];
  }

  emit('update:options', selectedOptions.value, isAlreadySelected && isMultiple ? undefined : optionValue);
};
</script>

<template>
  <div
    class="flex flex-nowrap"
    :class="{
      'flex-row': direction === 'row',
      'flex-col': direction === 'col'
    }"
  >
    <PopOver v-for="option in options" :key="option.value" :show="option.tooltip ? 'none' : 'hidden'">
      <button
        type="button"
        class="flex items-center justify-center px-4 py-2 m-1 text-sm rounded-full cursor-pointer border-2 transition-all duration-300 whitespace-nowrap disabled:cursor-not-allowed disabled:border-gray-200 disabled:text-gray-400"
        :class="{
          'border-primary text-primary': selectedOptions.includes(option.value),
          'border-gray-200': !selectedOptions.includes(option.value),
          '!cursor-not-allowed':
            (selectedOptions.length === minSelectedRequired && selectedOptions.includes(option.value)) ||
            requiredOptions.includes(option.value)
        }"
        :disabled="option.disabled"
        @click="() => handleOptionClick(option.value)"
        v-text="option.label"
      />
      <template #content>
        <div class="flex flex-col gap-2">{{ t(option.tooltip ?? '') }}</div>
      </template>
    </PopOver>
  </div>
</template>
